import { HttpClient } from './client/http-client';
import { SDKConfig } from './config/sdk-config';
import { APIRoute, API } from '@hero/api';
import {
  createCommonAPI,
  createCommonAuthMethods,
  createCommonOrderMethods,
  createCommonAnalyticsMethods,
  createCommonMemberMethods,
  createCommonPaymentMethods,
  createCommonLocationMethods,
  createCommonOrgProfileMethods,
  createCommonPriceMethods,
  createCommonClientConfigMethods,
  createCommonClientMethods,
  createCommonContactMethods,
  createCommonLeadPublicMethods,
  createCommonReferralMethods,
  createCommonSavedLocationMethods,
  createCommonRouteMethods,
  createCommonAgreementMethods,
  createCommonFinancingMethods,
  createCommonLenderMethods,
  createCommonConnectionMethods,
  createCommonProfileMethods,
  createCommonOrgMethods,
  createCommonEntityDirectoryMethods,
  createCommonSalesMethods,
  createCommonAgentMethods,
  createCommonCurrentUserMethods,
  createCommonMessageMethods,
} from './api/common';
import { isProduction } from './utils/env-detector';
import { APILoggingMetadata } from './utils/logging';

// Re-export for convenience
export type { APILoggingMetadata } from './utils/logging';

/**
 * Options for individual API calls
 */
export interface APICallOptions {
  headers?: Record<string, string>;
  timeout?: number;
  /** Internal logging metadata - stripped before API request */
  __logging?: APILoggingMetadata;
  [key: string]: any; // Allow other axios config options
}

/**
 * Type-safe interface representing methods shared across all role SDKs.
 * `useApi()` (no scope) returns this type so shared hooks can call
 * common methods without knowing which SDK is active.
 */
export interface CommonSDK {
  common: ReturnType<typeof createCommonAPI>;
  auth: ReturnType<typeof createCommonAuthMethods>;
  orders: ReturnType<typeof createCommonOrderMethods>;
  /**
   * Role-neutral analytics surface. Includes `getMyAnalyticsOverview`
   * (the lens-aware twin of `orders.getMyOrders`) — the single unified
   * path for home / dashboard tiles across every consumer. Role SDKs
   * may extend this with role-specific endpoints like
   * `getDriverAnalytics`.
   */
  analytics: ReturnType<typeof createCommonAnalyticsMethods>;
  members: ReturnType<typeof createCommonMemberMethods>;
  payment: ReturnType<typeof createCommonPaymentMethods>;
  location: ReturnType<typeof createCommonLocationMethods>;
  /**
   * Section-keyed, field-level partial profile reads and writes —
   * the canonical surface for org-profile updates.
   */
  orgProfile: ReturnType<typeof createCommonOrgProfileMethods>;
  price: ReturnType<typeof createCommonPriceMethods>;
  clientConfig: ReturnType<typeof createCommonClientConfigMethods>;
  /**
   * The unified `Client` CRM (relations-v2). Replaces the legacy
   * `customers` namespace.
   */
  clients: ReturnType<typeof createCommonClientMethods>;
  contacts: ReturnType<typeof createCommonContactMethods>;
  /**
   * Public lead-intake surface — `leads.submit` lets the landing page
   * post a "Book a demo" lead through any SDK without authentication.
   * Admin-only lead CRUD lives in the admin SDK's `leads` namespace.
   */
  leads: ReturnType<typeof createCommonLeadPublicMethods>;
  /**
   * Public referral surface — `referral.track` records a `?r=` visit,
   * `referral.resolve` looks a code up. Admin CRUD lives in the admin
   * SDK's `referral` namespace.
   */
  referral: ReturnType<typeof createCommonReferralMethods>;
  savedLocations: ReturnType<typeof createCommonSavedLocationMethods>;
  /** v2 network-model pricing routes (directed / open). */
  routes: ReturnType<typeof createCommonRouteMethods>;
  /** v2 metadata-only commercial agreements (Routes live separately). */
  agreements: ReturnType<typeof createCommonAgreementMethods>;
  /**
   * Embedded-financing v0 pilot. Same surface across every SDK
   * (carrier, customer, admin); the controller scopes by JWT +
   * `tenants[]` and the public `lender.*` methods are accessible
   * to every SDK so the same code path renders the lender table
   * from any app.
   */
  financing: ReturnType<typeof createCommonFinancingMethods>;
  /**
   * Admin-managed lender directory (financing partners). Public
   * `getActive` is included on every SDK so the carrier dashboard
   * can render the partners strip from any role.
   */
  lenders: ReturnType<typeof createCommonLenderMethods>;
  /** Relations-v2 — bilateral relationship lifecycle. */
  connections: ReturnType<typeof createCommonConnectionMethods>;
  /** Relations-v2 — global admin-curated brand directory. */
  profiles: ReturnType<typeof createCommonProfileMethods>;
  /** Relations-v2 — unified Business + Carrier read surface. */
  orgs: ReturnType<typeof createCommonOrgMethods>;
  /** Relations-v2 — unified Profile + Org picker search. */
  entityDirectory: ReturnType<typeof createCommonEntityDirectoryMethods>;
  /**
   * Hero-as-seller sales domain (subscriptions, billing,
   * billing-transactions, derived features). Mounted on every SDK so
   * the same `api.sales.*` namespace works in customer, carrier, and
   * admin apps.
   */
  sales: ReturnType<typeof createCommonSalesMethods>;
  /**
   * Hero AI agent — `sendMessage` returns an async iterator over
   * Server-Sent Events; `confirmMessage` is a normal JSON request.
   */
  agent: ReturnType<typeof createCommonAgentMethods>;
  /**
   * `current-user/*` — always-scoped-to-self endpoints for the
   * agent-facing signal layer (`User.meta`). See
   * `docs/ai/user-signals.md`.
   */
  currentUser: ReturnType<typeof createCommonCurrentUserMethods>;
  /**
   * Transactional messaging (WhatsApp waybill; email later).
   */
  message: ReturnType<typeof createCommonMessageMethods>;
}

/**
 * Core API class that provides base functionality for all domain SDKs
 * Includes common APIs that are available across all SDKs (customer, carrier, admin)
 */
export class CoreAPI {
  protected httpClient: HttpClient;
  protected config: SDKConfig;
  public readonly common: ReturnType<typeof createCommonAPI>;
  public readonly clients: ReturnType<typeof createCommonClientMethods>;
  public readonly contacts: ReturnType<typeof createCommonContactMethods>;
  public readonly leads: ReturnType<typeof createCommonLeadPublicMethods>;
  public readonly referral: ReturnType<typeof createCommonReferralMethods>;
  public readonly orders: ReturnType<typeof createCommonOrderMethods>;
  public readonly savedLocations: ReturnType<typeof createCommonSavedLocationMethods>;
  public readonly connections: ReturnType<typeof createCommonConnectionMethods>;
  public readonly profiles: ReturnType<typeof createCommonProfileMethods>;
  public readonly orgs: ReturnType<typeof createCommonOrgMethods>;
  public readonly entityDirectory: ReturnType<typeof createCommonEntityDirectoryMethods>;
  public readonly sales: ReturnType<typeof createCommonSalesMethods>;
  public readonly financing: ReturnType<typeof createCommonFinancingMethods>;
  public readonly lenders: ReturnType<typeof createCommonLenderMethods>;
  /**
   * Section-keyed `/org/profile` surface. Same instance shared by
   * every consumer SDK (carrier, customer, admin) — admins get
   * `adminGet` / `adminUpdate` overlaid in their own SDK.
   */
  public readonly orgProfile: ReturnType<typeof createCommonOrgProfileMethods>;
  public readonly agent: ReturnType<typeof createCommonAgentMethods>;
  public readonly currentUser: ReturnType<typeof createCommonCurrentUserMethods>;
  public readonly message: ReturnType<typeof createCommonMessageMethods>;

  constructor(config: SDKConfig) {
    this.config = {
      ...config,
      enableLogs: config.enableLogs !== undefined ? config.enableLogs : !isProduction(),
    };
    this.httpClient = new HttpClient(this.config);
    this.common = createCommonAPI(this);
    this.clients = createCommonClientMethods(this);
    this.contacts = createCommonContactMethods(this);
    this.leads = createCommonLeadPublicMethods(this);
    this.referral = createCommonReferralMethods(this);
    this.orders = createCommonOrderMethods(this);
    this.savedLocations = createCommonSavedLocationMethods(this);
    this.connections = createCommonConnectionMethods(this);
    this.profiles = createCommonProfileMethods(this);
    this.orgs = createCommonOrgMethods(this);
    this.entityDirectory = createCommonEntityDirectoryMethods(this);
    this.sales = createCommonSalesMethods(this);
    this.financing = createCommonFinancingMethods(this);
    this.lenders = createCommonLenderMethods(this);
    this.orgProfile = createCommonOrgProfileMethods(this);
    this.agent = createCommonAgentMethods(this);
    this.currentUser = createCommonCurrentUserMethods(this);
    this.message = createCommonMessageMethods(this);
  }

  /**
   * Update configuration (useful for token changes)
   */
  updateConfig(config: Partial<SDKConfig>): void {
    this.config = { ...this.config, ...config };
    this.httpClient = new HttpClient(this.config);
  }

  /**
   * Make an API call using an APIRoute
   * Returns just the data from the response
   */
  async call<T extends API<any, any>>(
    route: APIRoute,
    data?: T['req'],
    options?: APICallOptions,
  ): Promise<T['data']> {
    const url = route.endpoint(this.config.baseURL);
    const method = route.method;

    const response = await this.httpClient.requestWithMethod<T['res']>(method, url, data, options);
    return response.data;
  }

  /**
   * Make an API call using an APIRoute
   * Returns the full response object (with success, message, statusCode, etc.)
   */
  async callWithRes<T extends API<any, any>>(
    route: APIRoute,
    data?: T['req'],
    options?: APICallOptions,
  ): Promise<T['res']> {
    const url = route.endpoint(this.config.baseURL);
    const method = route.method;

    return this.httpClient.requestWithMethod<T['res']>(method, url, data, options);
  }
}
